import { useState, useEffect, useRef } from 'react';
import { fmoSpecService } from '../services/fmoSpecService';

/**
 * Custom hook for managing FMO spec state and fetching
 * Handles clusterspecs, pocketspecs, and inputspecs data
 */
export const useFMOSpecs = () => {
  // Specs data
  const [clusterspecs, setClusterspecs] = useState([]);
  const [pocketspecs, setPocketspecs] = useState([]);
  const [inputspecs, setInputspecs] = useState([]);
  
  // Selection states
  const [selectedClusterspec, setSelectedClusterspec] = useState('');
  const [selectedPocketspec, setSelectedPocketspec] = useState('');
  const [selectedInputspec, setSelectedInputspec] = useState('');
  
  // Inputspec details for dialog
  const [selectedInputspecDetails, setSelectedInputspecDetails] = useState(null);
  const [inputspecYaml, setInputspecYaml] = useState('');
  const [inputspecDialogOpen, setInputspecDialogOpen] = useState(false);
  
  // Store clusterspecs in ref to use in cluster mapping without triggering re-renders
  const clusterspecsRef = useRef([]);
  
  // Fetch specs function (reusable for initial load and refresh)
  const fetchSpecs = async (skipDefaults = false) => {
      try {
        // Fetch all specs using the service
        const { clusterspecs, pocketspecs, inputspecs } = await fmoSpecService.fetchAllSpecs();
        
        setClusterspecs(clusterspecs);
        setPocketspecs(pocketspecs);
        setInputspecs(inputspecs);
        
        // Store clusterspecs in ref for use in cluster mapping
        clusterspecsRef.current = clusterspecs;
        
        // Set default values if not already set (skip during refresh)
        if (!skipDefaults) {
          if (!selectedClusterspec && clusterspecs.length > 0) {
            const defaultClusterspec = fmoSpecService.findDefaultClusterspec(clusterspecs);
            if (defaultClusterspec) {
              setSelectedClusterspec(defaultClusterspec.id_);
            }
          }
          
          if (!selectedPocketspec && pocketspecs.length > 0) {
            const defaultPocketspec = fmoSpecService.findDefaultPocketspec(pocketspecs);
            if (defaultPocketspec) {
              setSelectedPocketspec(defaultPocketspec.id_);
            }
          }
          
          if (!selectedInputspec && inputspecs.length > 0) {
            const defaultInputspec = fmoSpecService.findDefaultInputspec(inputspecs);
            if (defaultInputspec) {
              setSelectedInputspec(defaultInputspec.id_);
            }
          }
        }
      
      // Return the fetched specs for immediate use
      return { clusterspecs, pocketspecs, inputspecs };
    } catch (error) {
      console.error('Failed to fetch specs:', error);
      throw error;
    }
  };
  
  // Refresh function that can be called externally (skips setting defaults)
  const refreshSpecs = async () => {
    return await fetchSpecs(true);
  };
  
  // Fetch specs on mount
  useEffect(() => {
    fetchSpecs();
  }, []);
  
  // Handle inspect inputspec
  const handleInspectInputspec = async (specId) => {
    try {
      // Fetch inputspec details using the service
      const { details, yaml } = await fmoSpecService.getInputspecDetails(specId);
      
      setSelectedInputspecDetails(details);
      setInputspecYaml(yaml);
      setInputspecDialogOpen(true);
      
      return { details, yaml };
    } catch (error) {
      console.error('Failed to fetch inputspec details:', error);
      throw error;
    }
  };
  
  // Auto-update dialog content when selection changes while dialog is open
  useEffect(() => {
    if (inputspecDialogOpen && selectedInputspec) {
      const updateDialogContent = async () => {
        try {
          const { details, yaml } = await fmoSpecService.getInputspecDetails(selectedInputspec);
          setSelectedInputspecDetails(details);
          setInputspecYaml(yaml);
        } catch (error) {
          console.error('Failed to update inputspec details:', error);
        }
      };
      updateDialogContent();
    }
  }, [selectedInputspec, inputspecDialogOpen]);
  
  // Reset selections
  const resetSpecSelections = () => {
    setSelectedClusterspec('');
    setSelectedPocketspec('');
    setSelectedInputspec('');
  };
  
  // Update selections from config
  const updateSelectionsFromConfig = (config) => {
    if (config.selectedClusterspec !== undefined) {
      setSelectedClusterspec(config.selectedClusterspec || '');
    }
    if (config.selectedPocketspec !== undefined) {
      setSelectedPocketspec(config.selectedPocketspec || '');
    }
    if (config.selectedInputspec !== undefined) {
      setSelectedInputspec(config.selectedInputspec || '');
    }
  };
  
  return {
    // Specs data
    clusterspecs,
    pocketspecs,
    inputspecs,
    clusterspecsRef,
    
    // Selection states
    selectedClusterspec,
    setSelectedClusterspec,
    selectedPocketspec,
    setSelectedPocketspec,
    selectedInputspec,
    setSelectedInputspec,
    
    // Inputspec details
    selectedInputspecDetails,
    inputspecYaml,
    inputspecDialogOpen,
    setInputspecDialogOpen,
    
    // Actions
    handleInspectInputspec,
    resetSpecSelections,
    updateSelectionsFromConfig,
    refreshSpecs
  };
};